import { splitLicensePlateForApi } from '../../../shared/utils/split-license-plate-for-api';
import {
  PurchaseOption,
  WayNormalizedPurchase,
} from '../models/way-purchase.models';
import { pickString, readPurchase, readRecord } from './way-purchase-display.helpers';

export const WAY_PURCHASE_PLATE_PLACEHOLDER = 'Enter plate number';

export const WAY_PURCHASE_PLATE_REQUIRED_MESSAGE =
  'License plate is required to redeem this purchase.';

/** Raw plate from notification / purchase (empty when missing). */
export function resolveWayPurchasePlateValue(
  data: Record<string, any> | null | undefined
): string {
  const row = readRecord(data);
  const purchase = readPurchase(row);
  return pickString(row?.['plateNumber'], row?.['plate'], purchase?.plate_number);
}

export function resolveWayPurchaseOptionPlateValue(option: PurchaseOption): string {
  if (option.source !== 'way') {
    return '';
  }
  return pickString(option.purchase?.plate_number);
}

export function resolveWayPurchaseLprEntryId(
  data: Record<string, any> | null | undefined
): string {
  const row = readRecord(data);
  return pickString(row?.['lpr_entry_id'], row?.['lprEntryId']);
}

/**
 * Attendant must enter a plate when confirming from phone/email lookup
 * (no LPR entry and no plate on the Way purchase).
 */
export function wayPurchaseNeedsPlateEntry(
  data: Record<string, any> | null | undefined
): boolean {
  if (resolveWayPurchaseLprEntryId(data)) {
    return false;
  }
  return !resolveWayPurchasePlateValue(data);
}

export function wayPurchaseOptionNeedsPlateEntry(
  data: Record<string, any> | null | undefined,
  option: PurchaseOption
): boolean {
  if (resolveWayPurchaseLprEntryId(data)) {
    return false;
  }
  if (option.source !== 'way') {
    return false;
  }
  return !resolveWayPurchaseOptionPlateValue(option);
}

export function normalizeWayPurchasePlateInput(value: string): string {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[\s-]/g, '');
}

export function isWayPurchasePlateInputValid(value: string): boolean {
  const parsed = splitLicensePlateForApi(normalizeWayPurchasePlateInput(value));
  return parsed.license_plate.length >= 2;
}

export function resolvePlateInputForWayOption(
  data: Record<string, any> | null | undefined,
  option: PurchaseOption,
  cardPlateInputs: Record<string, string>,
  cardId: string
): string {
  if (option.source !== 'way') {
    return '';
  }
  if (!wayPurchaseOptionNeedsPlateEntry(data, option)) {
    return resolveWayPurchaseOptionPlateValue(option);
  }
  return normalizeWayPurchasePlateInput(cardPlateInputs[cardId] ?? '');
}

/** Merge attendant-entered plate into confirm/notification context. */
export function mergeAttendantPlateIntoWayData(
  data: Record<string, any> | null | undefined,
  plateInput: string
): Record<string, any> | null {
  const row = readRecord(data);
  if (!row) {
    return null;
  }

  const normalized = normalizeWayPurchasePlateInput(plateInput);
  if (!normalized) {
    return { ...row };
  }

  const hintState = pickString(row['state'], row['plate_state']);
  const parsed = splitLicensePlateForApi(normalized, hintState || undefined);
  if (!parsed.license_plate) {
    return { ...row };
  }

  const purchase = readPurchase(row);
  const nextPurchase: WayNormalizedPurchase | null = purchase
    ? { ...purchase, plate_number: parsed.license_plate }
    : null;

  return {
    ...row,
    plate: parsed.license_plate,
    plateNumber: parsed.license_plate,
    state: parsed.state,
    ...(nextPurchase ? { purchase: nextPurchase } : {}),
  };
}
